/**
 * The recorded half of the audio, as node graphs.
 *
 * Sits beside `synth.ts` rather than replacing any of it. Both files build Web
 * Audio graphs and neither decides anything — the decisions (which recording an
 * archetype gets, how long it is allowed to sound, how loud a track loop is at
 * a given speed) are all in `policy.ts`, where they are unit-tested with no
 * audio device in sight.
 *
 * ### Everything here is optional, and nothing here is load-bearing
 *
 * A file that 404s, a `decodeAudioData` that rejects, a codec the browser does
 * not have: each ends with `get()` returning `null` and the caller playing the
 * synthesised cue instead. The game is fully audible with every one of these
 * files deleted, which is the property that lets recordings be added at all —
 * see `docs/audio.md`. Nothing in this file logs; a missing sound is a quieter
 * game, not an error, and the counters are on `window.__sowAudio()` for anyone
 * who wants to know.
 *
 * ### The same bus, the same limiter
 *
 * A recorded voice is built exactly like a synthesised one — envelope, panner,
 * out to whatever destination it is handed, which is the master bus feeding the
 * compressor. A sample path that reached the output on its own would let a
 * scattergun volley or six tanks dying at once sum straight past the cap that
 * exists to stop precisely that.
 *
 * ### Nothing here may be smoothed against frame time
 *
 * Same invariant as `synth.ts`. The track bed's gain moves with
 * `setTargetAtTime` on the audio clock, so the same tank at the same speed
 * arrives at the same loudness at the same wall-clock moment at 30 fps and at
 * 144 fps.
 */
import {
  SAMPLE_IDS,
  SAMPLE_SECONDS,
  SAMPLE_URLS,
  TRACK_LOOP_GLIDE,
  type SampleId,
  type SampleSpec,
} from './policy';

/** Matches `synth.ts`: exponential ramps refuse to reach zero, so they land here. */
const SILENCE = 0.0001;

/**
 * A decoded recording is only correct for the sample rate it was decoded at, and
 * a length that disagrees with `SAMPLE_SECONDS` by more than this means the file
 * on disk is not the file the mapping was written against.
 *
 * Reported rather than thrown: a re-cut asset should show up as a number on the
 * dev probe, not as a silent game.
 */
const LENGTH_TOLERANCE_SECONDS = 0.05;

/**
 * Fetches and decodes the recordings once per tab.
 *
 * Keyed on `SampleId` rather than on the URL because there are five of them and
 * they are named: this is a fixed catalogue, not a general asset cache.
 */
export class SampleBank {
  private readonly buffers = new Map<SampleId, AudioBuffer>();
  private readonly failures = new Map<SampleId, string>();
  private readonly inFlight = new Set<SampleId>();
  /** Assets whose decoded length disagrees with what policy was written against. */
  private readonly mismatched = new Map<SampleId, number>();

  /**
   * Starts loading everything. Idempotent, and safe to call on a suspended
   * context — `decodeAudioData` does not need the context to be running, which
   * is what lets the whole bank be warm before the player's first gesture.
   */
  loadAll(ctx: BaseAudioContext): void {
    for (const id of SAMPLE_IDS) void this.load(ctx, id);
  }

  private async load(ctx: BaseAudioContext, id: SampleId): Promise<void> {
    if (this.buffers.has(id) || this.inFlight.has(id) || this.failures.has(id)) return;
    this.inFlight.add(id);
    try {
      const response = await fetch(SAMPLE_URLS[id]);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const bytes = await response.arrayBuffer();
      const buffer = await decode(ctx, bytes);
      this.buffers.set(id, buffer);
      const expected = SAMPLE_SECONDS[id];
      if (Math.abs(buffer.duration - expected) > LENGTH_TOLERANCE_SECONDS) {
        this.mismatched.set(id, buffer.duration);
      }
    } catch (err) {
      // Every failure lands here and nowhere else: a 404, a decode refusal, a
      // codec the browser does not carry, a fetch blocked by a policy. The cue
      // falls back to its synthesised voice and the game carries on.
      this.failures.set(id, err instanceof Error ? err.message : 'failed');
    } finally {
      this.inFlight.delete(id);
    }
  }

  /** The decoded recording, or `null` while it is loading or if it never will. */
  get(id: SampleId): AudioBuffer | null {
    return this.buffers.get(id) ?? null;
  }

  /** Everything the dev probe reports about the bank. */
  report(): {
    loaded: number;
    failed: number;
    pending: number;
    buffers: Record<string, boolean>;
    failures: Record<string, string>;
    lengthMismatches: Record<string, number>;
  } {
    return {
      loaded: this.buffers.size,
      failed: this.failures.size,
      pending: this.inFlight.size,
      buffers: Object.fromEntries(SAMPLE_IDS.map((id) => [id, this.buffers.has(id)])),
      failures: Object.fromEntries(this.failures),
      lengthMismatches: Object.fromEntries(this.mismatched),
    };
  }
}

/**
 * `decodeAudioData` has two signatures and browsers disagree about which.
 *
 * The promise form is the modern one; Safari answered only the callback form for
 * years and still returns `undefined` from some builds. Handling both costs four
 * lines and removes a whole class of "no sound on one browser" report.
 */
function decode(ctx: BaseAudioContext, bytes: ArrayBuffer): Promise<AudioBuffer> {
  return new Promise<AudioBuffer>((resolve, reject) => {
    const maybe = ctx.decodeAudioData(bytes, resolve, reject);
    if (maybe && typeof maybe.then === 'function') maybe.then(resolve, reject);
  });
}

/**
 * Plays one recorded voice, and returns how many nodes it cost.
 *
 * The envelope is a click guard rather than a shape: a recorded gun already has
 * its own attack, and the only thing this has to do is stop the trim from being
 * a step discontinuity at either end. `spec.duration` is the *sounding* time, so
 * a rate below 1 makes the recording longer and the trim cuts more of it off —
 * which is what a heavier gun is, a bigger noise cut to the cadence it fires at.
 */
export function playSample(
  ctx: AudioContext,
  destination: AudioNode,
  buffer: AudioBuffer,
  spec: SampleSpec,
  gain: number,
  pan: number,
): number {
  const now = ctx.currentTime;
  let nodes = 0;

  const panner = ctx.createStereoPanner();
  panner.pan.value = Math.max(-1, Math.min(1, pan));
  panner.connect(destination);
  nodes += 1;

  const envelope = ctx.createGain();
  const peak = Math.max(gain, SILENCE);
  const attack = Math.max(spec.attack, 0.0005);
  // The trim never runs past the recording itself: a rate above 1 shortens it,
  // and asking for more sounding time than there is would schedule a fade over
  // silence and count a voice that is not making any noise.
  const available = buffer.duration / Math.max(spec.rate, 0.01);
  const sounding = Math.max(0.02, Math.min(spec.duration, available));
  const release = Math.min(spec.release, sounding * 0.6);

  envelope.gain.setValueAtTime(SILENCE, now);
  envelope.gain.linearRampToValueAtTime(peak, now + attack);
  envelope.gain.setValueAtTime(peak, now + Math.max(attack, sounding - release));
  envelope.gain.exponentialRampToValueAtTime(SILENCE, now + sounding);
  envelope.connect(panner);
  nodes += 1;

  const source = ctx.createBufferSource();
  source.buffer = buffer;
  source.playbackRate.value = Math.max(spec.rate, 0.01);
  source.connect(envelope);
  source.start(now);
  source.stop(now + sounding);
  nodes += 1;

  // Disconnected when it finishes, exactly as a synthesised voice is: left
  // attached, every shot fired in a match would stay on the master bus for the
  // life of the tab.
  source.onended = () => {
    envelope.disconnect();
    panner.disconnect();
  };

  return nodes;
}

// ─── The track bed ───────────────────────────────────────────────────────────

interface TrackVoice {
  source: AudioBufferSourceNode;
  gain: GainNode;
  panner: StereoPannerNode;
  /** Last gain asked for, so the probe can report what is actually audible. */
  level: number;
}

/**
 * One looping track rumble per tank, positioned.
 *
 * **Per tank rather than one global drone**, for the same reason every other
 * spatial cue is positioned: the interesting information in a track rumble is
 * that something heavy is moving *over there*, and a single loop gained by your
 * own speed throws all of it away.
 *
 * The loop is seamless by construction (`move-loop.webm` has its own head
 * crossfaded over its tail), so `loop = true` is genuinely gapless and there is
 * nothing to hide with a crossfade here. Each voice starts at a **random offset**
 * into the six seconds: six tanks starting at zero would be phase-locked into
 * one loud correlated drone rather than into a yard full of engines.
 *
 * Voices are created lazily and never restarted. A tank that stops still holds
 * its voice at zero gain — starting and stopping a source every time somebody
 * pauses on a ridge is a click each way, and a stopped `AudioBufferSourceNode`
 * cannot be restarted anyway.
 */
export class TrackBed {
  private readonly voices = new Map<string, TrackVoice>();
  private stopped = false;

  constructor(
    private readonly ctx: AudioContext,
    private readonly destination: AudioNode,
    private readonly buffer: AudioBuffer,
  ) {}

  /** Voices currently built. Not the same as voices currently audible. */
  get size(): number {
    return this.voices.size;
  }

  /** How many are actually making a noise, for the dev probe. */
  audible(): number {
    let count = 0;
    for (const voice of this.voices.values()) if (voice.level > 0.001) count += 1;
    return count;
  }

  /**
   * Sets one tank's rumble.
   *
   * Zero gain does not build a voice — a tank sitting still at the far end of
   * the map costs nothing until it moves — but it does glide an existing one
   * down rather than cutting it.
   */
  set(id: string, gain: number, pan: number): void {
    if (this.stopped) return;
    const existing = this.voices.get(id);
    if (!existing && gain <= 0) return;
    const voice = existing ?? this.build();
    if (!existing) this.voices.set(id, voice);
    voice.level = gain;
    const now = this.ctx.currentTime;
    voice.gain.gain.setTargetAtTime(Math.max(0, gain), now, TRACK_LOOP_GLIDE);
    voice.panner.pan.setTargetAtTime(Math.max(-1, Math.min(1, pan)), now, TRACK_LOOP_GLIDE);
  }

  /** Drops a tank's voice — it left the arena, or was destroyed. */
  release(id: string): void {
    const voice = this.voices.get(id);
    if (!voice) return;
    this.voices.delete(id);
    this.fadeOut(voice);
  }

  /**
   * Drops every voice whose tank was not reported this frame.
   *
   * A tank that left the arena stops being mentioned rather than announcing
   * itself, so this is what keeps a match's worth of departures from leaving a
   * yard of silent looping sources attached to the bus.
   */
  retain(keep: ReadonlySet<string>): void {
    for (const id of [...this.voices.keys()]) {
      if (!keep.has(id)) this.release(id);
    }
  }

  dispose(): void {
    if (this.stopped) return;
    this.stopped = true;
    for (const voice of this.voices.values()) this.fadeOut(voice);
    this.voices.clear();
  }

  private build(): TrackVoice {
    const ctx = this.ctx;
    const now = ctx.currentTime;

    const panner = ctx.createStereoPanner();
    panner.connect(this.destination);

    const gain = ctx.createGain();
    gain.gain.value = 0;
    gain.connect(panner);

    const source = ctx.createBufferSource();
    source.buffer = this.buffer;
    source.loop = true;
    source.connect(gain);
    source.start(now, Math.random() * this.buffer.duration);

    return { source, gain, panner, level: 0 };
  }

  private fadeOut(voice: TrackVoice): void {
    const now = this.ctx.currentTime;
    voice.gain.gain.cancelScheduledValues(now);
    voice.gain.gain.setTargetAtTime(0, now, 0.03);
    try {
      voice.source.stop(now + 0.2);
    } catch {
      // Already stopped. Nothing to do and nothing worth saying.
    }
    voice.source.onended = () => {
      voice.gain.disconnect();
      voice.panner.disconnect();
    };
  }
}
